import clsx from "clsx";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import {
  createEmptySegment,
  type BreakdownGroupDescriptor,
  type SegmentDescriptor,
} from "@north-beam/nb-common";
import { SecondaryButton } from "@shared/buttons";
import { Switch } from "@shared/switch";

import { NameInput } from "./name-input";
import { SegmentRow } from "./segment-row";

interface Props {
  showSegmentActiveSwitch?: boolean;
  breakdownGroup: BreakdownGroupDescriptor;
  onChange: (value: BreakdownGroupDescriptor) => void;
  onClickShowMatches: (value: SegmentDescriptor) => void;
  onSwitchToAdvancedEditor?: () => void;
}

export const LabelRuleWizard = ({
  showSegmentActiveSwitch,
  breakdownGroup,
  onChange,
  onClickShowMatches,
  onSwitchToAdvancedEditor,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const oldIndex = breakdownGroup.segments.findIndex(
      ({ id }) => id === active.id,
    );
    const newIndex = breakdownGroup.segments.findIndex(
      ({ id }) => id === over?.id,
    );

    const newOrdering = arrayMove(breakdownGroup.segments, oldIndex, newIndex);

    prettyLogEventPage({
      domain: "Breakdown v2",
      action: "onChange",
      additionalActionInfo: "Reorder segments",
      data: {
        newSegments: newOrdering,
        pageContext: { segments: breakdownGroup.segments },
      },
    });

    onChange({ ...breakdownGroup, segments: newOrdering });
  };

  return (
    <div>
      <div className="mb-5">
        <div className="text-xs text-gray uppercase">Breakdown name</div>
        <div className="mb-1.5 flex items-center justify-between">
          <div>
            <NameInput
              value={breakdownGroup.name}
              onChange={(value) => onChange({ ...breakdownGroup, name: value })}
            />
            {breakdownGroup.error && (
              <div className="text-xxs text-mars mt-1">
                *{breakdownGroup.error}
              </div>
            )}
          </div>
          {onSwitchToAdvancedEditor && (
            <div className="text-sm flex items-center gap-2">
              <Switch
                id="switch-to-advanced-editor-wizard"
                size="small"
                value={false}
                onChange={() => {
                  prettyLogEventPage({
                    domain: "Breakdown v2",
                    action: "onChange",
                    additionalActionInfo: "Switch to advanced editor",
                  });

                  onSwitchToAdvancedEditor();
                }}
              />
              Advanced Editor
            </div>
          )}
        </div>
        <p className="text-gray text-sm">
          Campaign/adset/ads can only match with one segment. Northbeam will
          search 30 days of data for matches in descending order.
        </p>
      </div>
      <div className="space-y-2 mb-2">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext items={breakdownGroup.segments.map((e) => e.id)}>
            {breakdownGroup.segments.map((segment, i) => (
              <SegmentRow
                key={segment.id}
                showActiveSwitch={showSegmentActiveSwitch}
                breakdownGroup={breakdownGroup}
                segment={segment}
                index={i}
                totalSegments={breakdownGroup.segments.length}
                onChange={(value) => {
                  onChange({
                    ...breakdownGroup,
                    segments: breakdownGroup.segments.map((e) =>
                      e.id === value.id ? value : e,
                    ),
                  });
                }}
                onDelete={() => {
                  onChange({
                    ...breakdownGroup,
                    segments: breakdownGroup.segments.filter(
                      (e) => e.id !== segment.id,
                    ),
                  });
                }}
                onClickShowMatches={() => {
                  prettyLogEventPage({
                    domain: "Breakdown v2",
                    action: "onClick",
                    additionalActionInfo: "Show matches",
                    data: {
                      pageContext: { segment: segment },
                    },
                  });

                  onClickShowMatches(segment);
                }}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
      <div
        className={clsx(
          "border border-[white] p-2 bg-white flex items-center gap-3",
        )}
      >
        <i
          className={clsx("fa-solid fa-arrow-turn-down-right text-gray-2", {
            "ml-[32px]": !showSegmentActiveSwitch,
            "ml-[42px] mr-[6px]": showSegmentActiveSwitch,
          })}
        />
        <SecondaryButton
          className="font-bold h-8 py-0 flex items-center gap-2"
          onClick={() => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onChange",
              additionalActionInfo: "Add segment",
            });

            onChange({
              ...breakdownGroup,
              segments: [...breakdownGroup.segments, createEmptySegment()],
            });
          }}
        >
          <i className="fa-regular fa-circle-plus text-cosmic" />
          ADD SEGMENT
        </SecondaryButton>
      </div>
    </div>
  );
};
